import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Privacy.module.css";

const UPDATED = "October 24, 2026";
// Confirm with the team which inbox answers privacy requests before publishing.
const CONTACT_EMAIL = "esclab.ub@gmail.com";

/** Privacy policy: what mRehab collects, why, where it is kept, and how to delete it. */
export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy policy | mRehab";
  }, []);

  return (
    <main className="ui-page">
      <article className={`ui-container ${styles.doc}`}>
        <h1 className={styles.title}>Privacy policy</h1>
        <p className={styles.updated}>Last updated {UPDATED}</p>

        <p>
          mRehab is a stroke-rehabilitation app and website run by the ESC lab at the University at Buffalo. This
          page explains what information mRehab collects, why, where it is kept, who can see it, and how to delete it.
          It covers the mRehab mobile app and this website.
        </p>

        <h2>What we collect</h2>
        <ul>
          <li>
            <b>Account details:</b> your email address, and your name if you give it.
          </li>
          <li>
            <b>Profile details you choose to give:</b> birth year, which side is affected, gender, and a shipping
            address so we can send you the mRehab mug and bowl kit.
          </li>
          <li>
            <b>Exercise results:</b> which exercise you did, with which hand, when, and how it went (repetitions,
            scores, accuracy, duration).
          </li>
          <li>
            <b>Motion recordings:</b> readings from your phone's motion sensors while you exercise, used to measure
            your movement.
          </li>
          <li>
            <b>Device details:</b> your phone model, operating system and app version, so we can fix problems.
          </li>
          <li>
            <b>Voice commands</b> (only where the app's voice assistant is available and you use it): what you said
            and a recording of it, so we can understand and improve the assistant.
          </li>
          <li>
            <b>App usage and crash reports:</b> which screens are used and details of crashes, collected through
            Google Firebase.
          </li>
          <li>
            <b>Reminders:</b> exercise reminders are scheduled on your phone; we don't receive them.
          </li>
          <li>
            <b>Orders and messages:</b> if you buy from our shop or contact us, your order (items, shipping address)
            or your message. Payments are handled by Stripe; we never see or store your card details.
          </li>
        </ul>

        <h2>Why we use it</h2>
        <ul>
          <li>To run the app: your account, your exercises and your progress.</li>
          <li>To ship your kit and handle orders.</li>
          <li>To let the clinicians you choose see your progress.</li>
          <li>To study and improve stroke rehabilitation and the app itself.</li>
          <li>To find and fix problems.</li>
        </ul>
        <p>
          We do not sell your information and we do not use it for advertising. The app does not collect your
          location.
        </p>

        <h2>Who can see it</h2>
        <ul>
          <li>The mRehab team at the University at Buffalo.</li>
          <li>
            Clinicians or others <b>you</b> choose to share your data with in the app. You can see who you share
            with.
          </li>
          <li>
            Services that run mRehab for us: Amazon Web Services (storage and email), Google Firebase (usage and crash
            reports) and Stripe (payments). They handle data only to provide their service.
          </li>
        </ul>

        <h2>Where it is kept</h2>
        <p>
          Your information is stored with Amazon Web Services in the United States, encrypted when stored. Backups
          are kept for up to 35 days, and earlier versions of recorded files for up to 90 days.
        </p>

        <h2>How we protect it</h2>
        <p>
          Your account is protected by sign-in, data is encrypted when stored, and connections to mRehab are
          encrypted. Only the mRehab team manages accounts and study data.
        </p>

        <h2>Emails we send</h2>
        <p>
          We only send emails you need: sign-in codes, your access code when your request to join is approved, and
          account emails such as password resets. We do not send newsletters or marketing.
        </p>

        <h2>Deleting your data</h2>
        <p>
          You can delete your account at any time from your account settings on this website. This removes your
          account, profile, exercise results, motion recordings, voice commands and sharing settings. Order records
          are kept because they are payment and shipping records. Deleted data also leaves our backups within 35
          days, and earlier file versions within 90 days.
        </p>

        <h2>Children</h2>
        <p>
          mRehab is designed for adults and is not directed at children under 13. We do not knowingly collect
          information from children under 13; if we learn we have, we will delete it. Parents who believe their child
          has used mRehab can contact us.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          If we change how we handle your information, we will update this page and the date at the top. Important
          changes will also be shown in the app.
        </p>

        <h2>Questions</h2>
        <p>
          For any question about your data, or to ask us to delete it, email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or <Link to="/contact">contact us</Link>.
        </p>
      </article>
    </main>
  );
}
